import { Box, Button, ColumnConfig, DataTable, Form, FormField, Layer, Spinner, Text, TextInput } from "grommet";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import CreateRolesBarCloser from "../../../../../../assets/close_small.svg";
import { useStore } from "../../../../../../Hooks/useStore";
import rolesService from "./roles.service";
import { RoleType } from "./types/RoleType";
import { Mixpanel } from "../../../../../../util/Helper";
import { PermissionType } from "../Permissions/types/PermissionType";
import permissionsService from "../Permissions/permissions.service";
import Pagination from "../../../common/Pagination/sidebarPagination.tsx/Pagination";
import usersService from "../Users/users.service";
import './CreateRolesBar.css';

function CreateRolesBar() {
  const { rootStore } = useStore();
  const navigate = useNavigate();
  const fromRole = useLocation().state as RoleType;

  const [inputValue, setInputValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const handleSubmit = () => {
    console.log('Form submitted with value:', inputValue);
  };


  useEffect(() => {
    if (fromRole) {
      rootStore.adminStore.handleSelectedPermission(fromRole.permissions.map(permission => permission.code));
    }
  }, [fromRole]);

  const batchAddUsers = useCallback(async () => {
    let uInfos = localStorage.getItem("userinfo");
    if (uInfos) {
    const userinformation = JSON.parse(uInfos);
      Mixpanel.track("web_add_user_batch_click",{ name: userinformation?.username });
    }
    try {
      await usersService.batchUserCreation(rootStore.adminStore.getUserFormFile);
      rootStore.notificationToastStore.showNotification("Roles Created Successfully!", false, 3000);
    } catch (e) {
      console.log("error occurred adding batch", e);
      if (e instanceof AxiosError) {
        rootStore.errorHandlingStore.displayError(e.message);
      } else {
        rootStore.notificationToastStore.showNotification("Error occurred while adding users", true, 3000);
      }
    }
  }, [rootStore.adminStore.getUserFormFile]);

  const columns = useMemo(() => [
    {
      align:"left",
      property: "code",
      header: "Permission Code",
      primary: true,
      render: item => <div className="user-cell-container">{item.code}</div>,
    },
    {
      property: "description",
      header: "Description",
      render: item => <div className="user-cell-container">{item.description}</div>,
    },
  ] as ColumnConfig<PermissionType>[], []);

  useEffect(() => {
    permissionsService
      .listAllPermissions()
      .then((permissions: PermissionType[]) => {
        if (permissions.length === 0) {
          rootStore.errorHandlingStore.displayError("Error: unable to load permissions, please reload the page!");
        } else {
          rootStore.adminStore.handlePermissions(permissions);
        }
      })
      .catch(error => {
        rootStore.errorHandlingStore.displayError(JSON.stringify(error));
      });
  }, []);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const totalItems = rootStore.adminStore.getPermissions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  
  // Calculate data to display based on the current page
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = rootStore.adminStore.getPermissions.slice(startIndex, endIndex);

  return (
    <Layer
      className="create-role-sidebar-container"
      style={{ borderRadius: "12px 0px 0px 0px", width: "700px", height: "100%" }}
      full="vertical"
      position="right"
      onEsc={() => rootStore.adminStore.handleRolesShowCloseButton(false) }
      onClickOutside={() => rootStore.adminStore.handleRolesShowCloseButton(false)}

    >
      <Box
        fill
        style={{ minWidth: "378px", borderRadius: "12px 0px 0px 0px", maxWidth: "700px" }}
        flex
        flex-start
        direction="column"
        align="center"
      >
        <Box
          style={{
            borderRadius: "12px 0px 0px 0px",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between"
          }}
          direction="column"
          as="header"
          background={"#E9F2FF"}
          width="100%"
          pad={"small"}
        >
          <Text
            style={{
              color: "#4B628A",
              fontFamily: "Inter",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: "600",
              lineHeight: "20px"
            }}
          >
            Create New Role
          </Text>
          <Button
            className="close-btn"
            style={{
              color: "#357EFF",
              fontFamily: "Inter",
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: "600",
              lineHeight: "16px"
            }}
            onClick={() => rootStore.adminStore.handleRolesShowCloseButton(false)}
          >
            <img src={CreateRolesBarCloser} style={{ width: 20, height: 20 }} alt="Close" />
            Close
          </Button>
        </Box>
        <Box className="create-Roles-middle-container">
          <Form
            style={{ width: "700px", padding: "20px", overflow: 'auto' }}
            validate="change"
            onSubmit={({ value }: { value: any }) => {
              let uInfos = localStorage.getItem("userinfo");
              if (uInfos) {
              const userinformation = JSON.parse(uInfos);
              Mixpanel.track("web_create_role_submit_click",{name: userinformation?.username});
              }
              const { code, displayName } = value;
              if (rootStore.adminStore.getSelectedPermission.length === 0) {
                rootStore.adminStore.handleValidationError("Please select at least 1 permission!");
              } else {
                const new_role: RoleType = {
                  code,
                  displayName,
                  permissions: [],
                  isStandard: false,
                };

                rolesService
                  .createRole(new_role)
                  .then(async (res) => {
                    const assignmentRequests = Promise.all(
                      rootStore.adminStore.getSelectedPermission.map((permissionCode) =>
                        rolesService.assignPermission(new_role.code, permissionCode as string)
                      )
                    );
                    try {
                      
                      await assignmentRequests;
                      rootStore.notificationToastStore.showNotification("Role created and permissions assigned successfully!", false, 3000);
                 
                      batchAddUsers();
                      rootStore.roleStore.handleupdates()
                      rootStore.adminStore.handleRolesShowCloseButton(false);

                    } catch (e) {
                      if (e instanceof AxiosError) {
                        rootStore.errorHandlingStore.displayError(e.message);
                      } else {
                        rootStore.errorHandlingStore.displayError(
                          "Error occurred while assigning permissions to the role: " + new_role.code
                        );
                      }
                    }
                  })
                  .catch((error: AxiosError) => {
                    rootStore.errorHandlingStore.displayError(error.message);
                  });
              }
            }}
            className="create-Role-Form"
          >
            <FormField
              className="create-Role-Form-F1"
              name="code"
              htmlFor="textinput-id"
              required
              validate={[
                {
                  regexp: /^[a-zA-Z0-9]+$/,
                  message: "Role Name cannot contain special characters and space.",
                },
              ]}
            >
              <label
                style={{
                  color: "#4B628A",
                  fontFeatureSettings: "'clig' off, 'liga' off",
                  fontFamily: "Inter",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: "500",
                  lineHeight: "22px",
                  alignSelf: "stretch"
                }}
                className="Create-Role-Form-L1"
              >
                Role Name
              </label>
              <TextInput
                style={{
                  display: "flex",
                  padding: "13px 15px",
                  alignItems: "flex-start",
                  gap: "10px",
                  alignSelf: "stretch",
                  borderRadius: "10px",
                 
                  color: "#41454B",
                  fontFamily: "Inter",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: "600",
                  lineHeight: "22px"
                }}
                name="code"
                placeholder="Role Name"
                maxLength={50}
              />
            </FormField>
            <FormField
              className="Create-Role-Form-F2"
              name="displayName"
              htmlFor="textinput-id"
              required
            >
              <label
                style={{
                  color: "#4B628A",
                  fontFeatureSettings: "'clig' off, 'liga' off",
                  fontFamily: "Inter",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: "500",
                  lineHeight: "22px",
                  alignSelf: "stretch"
                }}
                className="Create-Role-Form-L1"
              >
                Description
              </label>
              <TextInput
                style={{
                  display: "flex",
                  padding: "13px 15px",
                  alignItems: "flex-start",
                  gap: "10px",
                  alignSelf: "stretch",
                  borderRadius: "10px",
                  background: "#F2F8FF",
                  height: "80px",
                  borderBottom: "none",
                  color: "#41454B",
                  fontFamily: "Inter",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: "600",
                  lineHeight: "22px"
                }}
                id="textinput-id"
                name="displayName"
                placeholder="Enter Here"
              />
            </FormField>
            <DataTable
              id="table-id"
              className="user-list-table-container"
              columns={columns}
              data={currentData} // Use currentData for pagination
              resizeable
              step={10}
              sortable
              title="Permissions"
              onSelect={(items) => {
                if (items.length > 0) {
                  rootStore.adminStore.handleValidationError("");
                }
                rootStore.adminStore.handleSelectedPermission(items);
              }}
              select={rootStore.adminStore.getSelectedPermission}
            />
            {rootStore.adminStore.getPermissions.length === 0 ? (
              <div className="spinner-container">
                <Spinner
                  border={[
                    { side: "all", color: "transparent", size: "medium" },
                    { side: "horizontal", color: "brand", size: "medium" },
                  ]}
                />
              </div>
            ) : null}
            {rootStore.adminStore.getValidationError !== "" ? (
              <p className="validation-text">{rootStore.adminStore.getValidationError}</p>
            ) : null}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              onPageChange={handlePageChange}
            />
            <Box
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "10px",
                width: "660px",
                justifyContent: "flex-end",
                paddingTop: "12px"
              }}
            >
              <Button
                style={{
                  display: "flex",
                  padding: "14px 49px",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "10px",
                  borderRadius: "10px",
                  border: "1px solid #357EFF",
                  background: "#FFF",
                  color: "#357EFF",
                  fontFamily: "Inter",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: "600",
                  lineHeight: "24px",
                }}
                type="reset"
                onClick={() => {
                  let uInfos = localStorage.getItem("userinfo");
                  if (uInfos) {
                  const userinformation = JSON.parse(uInfos);
                  Mixpanel.track("web_create_role_reset_click",{name: userinformation?.username});
                  }
                  rootStore.adminStore.resetSelectedPermission();
                }}
                label="Reset"
              />
              <Button
                style={{
                  display: "flex",
                  padding: "14px 49px",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "10px",
                  borderRadius: "10px",
                  background: "#357EFF",
                  color: "var(--all-colors-white-color-white, #FFF)",
                  fontFamily: "Inter",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: "600",
                  lineHeight: "24px",
                }}
                type="submit"
                label="Assign Roles"
           
              />
            </Box>
          </Form>
        </Box>
      </Box>
    </Layer>
  );
}

export default observer(CreateRolesBar);
